import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { processApproval } from '../../store/slices/contractsSlice';
import type { AppDispatch } from '../../store';
import type { Contract } from '../../types/contracts';

interface ApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: Contract;
  action: 'approve' | 'reject';
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({ isOpen, onClose, contract, action }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await dispatch(
        processApproval({
          contractId: contract.id,
          action: {
            action: action as 'approve' | 'reject',
            comments: comments.trim() || (action === 'reject' ? 'No reason provided' : undefined),
          },
        })
      ).unwrap();

      onClose();
      setComments('');
    } catch (error) {
      console.error(`Failed to ${action} contract:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getActionText = () => (action === 'approve' ? 'Approve' : 'Reject');

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium text-gray-900">{getActionText()} Contract</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Contract Summary */}
        <div className="mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Contract Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Contract Number:</span>
                <span className="ml-2 font-medium">{contract.contractNumber}</span>
              </div>
              <div>
                <span className="text-gray-600">Title:</span>
                <span className="ml-2 font-medium">{contract.title}</span>
              </div>
              <div>
                <span className="text-gray-600">Amount:</span>
                <span className="ml-2 font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: contract.currency,
                  }).format(contract.amount)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Party:</span>
                <span className="ml-2 font-medium">{contract.party?.name}</span>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Action confirmation */}
          <div
            className={`mb-6 p-4 rounded-lg border-l-4 ${
              action === 'approve' ? 'bg-green-50 border-green-400' : 'bg-red-50 border-red-400'
            }`}
          >
            <div className="flex">
              <div className="ml-3">
                <h3
                  className={`text-sm font-medium ${
                    action === 'approve' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {action === 'approve'
                    ? 'You are about to approve this contract'
                    : 'You are about to reject this contract'}
                </h3>
                <div
                  className={`mt-2 text-sm ${
                    action === 'approve' ? 'text-green-700' : 'text-red-700'
                  }`}
                >
                  <p>
                    {action === 'approve'
                      ? 'Approving will move the contract to the next step in the approval workflow or mark it as fully approved if this is the final step.'
                      : 'Rejecting will halt the approval process and return the contract to draft status for revisions.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comments {action === 'reject' && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={comments}
              onChange={e => setComments(e.target.value)}
              rows={4}
              placeholder={
                action === 'approve'
                  ? 'Optional: Add any comments about your approval...'
                  : 'Please provide a reason for rejecting this contract...'
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange resize-none"
              required={action === 'reject'}
            />
            {action === 'reject' && (
              <p className="mt-1 text-sm text-gray-500">
                Rejection reason is required and will be visible to the contract creator.
              </p>
            )}
          </div>

          {/* Security notice */}
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg
                className="w-5 h-5 text-yellow-400 mt-0.5 mr-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Audit Trail</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>This action will be recorded in the contract audit trail including:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Your user ID and timestamp</li>
                    <li>IP address and browser information</li>
                    <li>Action taken ({getActionText().toLowerCase()}) and comments</li>
                    <li>Contract state before and after the action</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (action === 'reject' && !comments.trim())}
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed ${
                action === 'approve' ? 'bg-green-600' : 'bg-red-600'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                getActionText()
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApprovalModal;
